import { findScrollParent } from "./findScrollParent";

export type SmoothScrollAlign = "frameTop" | "frameEnd";

export interface SmoothScrollOptions {
  align?: SmoothScrollAlign;
  durationMs?: number;
  offsetPx?: number;
}

let activeScrollCancel: (() => void) | null = null;

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function getScrollTop(scrollParent: HTMLElement): number {
  if (scrollParent === document.documentElement) {
    return window.scrollY || document.documentElement.scrollTop;
  }
  return scrollParent.scrollTop;
}

function setScrollTop(scrollParent: HTMLElement, value: number): void {
  if (scrollParent === document.documentElement) {
    window.scrollTo({ top: value, behavior: "auto" });
  } else {
    scrollParent.scrollTop = value;
  }
}

function computeTargetScroll(
  element: HTMLElement,
  scrollParent: HTMLElement,
  align: SmoothScrollAlign,
  offsetPx: number
): number {
  const elRect = element.getBoundingClientRect();

  if (scrollParent === document.documentElement) {
    const scrollTop = getScrollTop(scrollParent);
    if (align === "frameTop") {
      return Math.max(0, scrollTop + elRect.top - offsetPx);
    }
    return Math.max(0, scrollTop + elRect.bottom - window.innerHeight + offsetPx);
  }

  const parentRect = scrollParent.getBoundingClientRect();
  const current = scrollParent.scrollTop;
  if (align === "frameTop") {
    return Math.max(0, current + (elRect.top - parentRect.top) - offsetPx);
  }
  return Math.max(0, current + (elRect.bottom - parentRect.bottom) + offsetPx);
}

/** Rolagem suave; cancela animação anterior se houver. */
export function smoothScrollElement(
  element: HTMLElement | null,
  options: SmoothScrollOptions = {}
): void {
  if (!element) return;

  activeScrollCancel?.();
  activeScrollCancel = null;

  const { align = "frameTop", durationMs = 1000, offsetPx = 8 } = options;
  const scrollParent = findScrollParent(element);
  const target = computeTargetScroll(element, scrollParent, align, offsetPx);
  const reduced = prefersReducedMotion();
  const duration = reduced ? Math.min(150, durationMs) : durationMs;

  const startScroll = getScrollTop(scrollParent);
  const delta = target - startScroll;
  if (Math.abs(delta) < 1) return;

  if (reduced || duration <= 0) {
    setScrollTop(scrollParent, target);
    return;
  }

  let rafId = 0;
  let cancelled = false;
  const startTime = performance.now();

  const cancel = () => {
    cancelled = true;
    if (rafId) cancelAnimationFrame(rafId);
  };
  activeScrollCancel = cancel;

  const tick = (now: number) => {
    if (cancelled) return;
    const t = Math.min(1, (now - startTime) / duration);
    setScrollTop(scrollParent, startScroll + delta * easeInOutCubic(t));
    if (t < 1) {
      rafId = requestAnimationFrame(tick);
    } else {
      activeScrollCancel = null;
    }
  };

  rafId = requestAnimationFrame(tick);
}
